import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { API } from '../../data/API/API';
import Datalist, { ListColumn } from '../Datalist/Datalist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import StyledAccordion from '../StyledAccordion/StyledAccordion'; // Import StyledAccordion
import './AValueListModal.scss';

interface AValueListModalProps {
    orgNr: string;
    isOpen: boolean;
    onClose: () => void;
    thresholdRed?: number;
    thresholdYellow?: number;
    selectedDate?: Date;
}

interface Contract {
    contract: string;
    diffByMax24: number;
}

interface MonthData {
    year: number;
    month: number;
    contracts: Contract[];
}

const AValueListModal: React.FC<AValueListModalProps> = ({ orgNr, isOpen, onClose, thresholdRed, thresholdYellow, selectedDate }) => {
    const [diffData, setDiffData] = useState<MonthData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getMonthName = (monthNumber: number) => {
        const date = new Date();
        date.setMonth(monthNumber - 1);
        const monthName = new Intl.DateTimeFormat('sv-SE', { month: 'long' }).format(date);
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    };

    useEffect(() => {
        if (isOpen) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const response = await API.Suppliers.GetDiffByMax24(orgNr); // Fetch without selectedDate

                    // Sort the response so that the most recent month is at the top
                    const sortedData = response.sort((a: MonthData, b: MonthData) => {
                        const aDate = new Date(a.year, a.month - 1);
                        const bDate = new Date(b.year, b.month - 1);
                        return bDate.getTime() - aDate.getTime(); // Sort in descending order
                    });

                    setDiffData(sortedData || []);
                    setLoading(false);
                } catch (err) {
                    setError('Misslyckades med att hämta data');
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [orgNr, isOpen]);

    // Define the columns for the Datalist table
    const columns: ListColumn<Contract>[] = React.useMemo(() => [
        {
            key: 'contract',
            fieldName: 'contract',
            name: 'Kontrakt',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Contract) => item.contract,
        },
        {
            key: 'diffByMax24',
            fieldName: 'diffByMax24',
            name: 'A.Värde',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Contract) => {
                const aValue = item.diffByMax24;

                if (thresholdRed !== undefined && thresholdYellow !== undefined) {
                    let color = '#07A235'; // Default green
                    let icon = faCheckCircle;

                    if (aValue > thresholdRed) {
                        color = '#DC143C'; // Red
                        icon = faTimesCircle;
                    } else if (aValue > thresholdYellow) {
                        color = '#F5A32D'; // Yellow
                        icon = faExclamationCircle;
                    }

                    return (
                        <div className="a-value-container">
                            <span className="a-value-text">{aValue.toFixed(2)}%</span>
                            <FontAwesomeIcon icon={icon} className="a-value-icon" style={{ color }} />
                        </div>
                    );
                } else {
                    return <span>{aValue.toFixed(2)}%</span>;
                }
            }
        }
    ], [thresholdRed, thresholdYellow]);

    return (
        <Modal show={isOpen} onHide={onClose} centered dialogClassName="custom-modal-width">
            <Modal.Header style={{backgroundColor:'#173c53',color:'white'}} closeButton>
                <Modal.Title>A.Värden för de senaste 12 månaderna</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{backgroundColor:'#fffaf5'}}>
                {loading ? (
                    <Spinner animation="border" />
                ) : error ? (
                    <p>{error}</p>
                ) : (
                    <div className="diff-by-max24-list">
                        {diffData.length > 0 ? (
                            diffData.map((monthData, index) => {
                                const isSelectedMonth = selectedDate
                                    ? monthData.year === selectedDate.getFullYear() && monthData.month === (selectedDate.getMonth() + 1)
                                    : false;
                                return (
                                    <StyledAccordion
                                        key={index}
                                        title={`${monthData.year} ${getMonthName(monthData.month)}`}
                                        open={isSelectedMonth}
                                    >
                                        <div className="custom-table-width">
                                            <Datalist
                                                items={monthData.contracts}
                                                isLoading={false}
                                                columns={columns}
                                                setColumns={() => { }}  // Provide a no-op function if no updates to columns are necessary
                                                initialSortKey="diffByMax24"
                                                descendingSort={false}
                                                noItemsText="Inga kontrakt tillgängliga"
                                                uniqueKey="contract"
                                            />

                                        </div>
                                    </StyledAccordion>
                                );
                            })
                        ) : (
                            <p>Ingen data tillgänglig för visning</p>
                        )}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AValueListModal;
